import React from "react";
//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceConstants } from "utils/constants";
import { InvoiceFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { CurrencyEnums, DimensionEnums } from "models/enums";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function useQuotationInitialValues(noVat: boolean, quotation?: WintSuperkollAPIModel.Quotation) {
  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);
  const allowedDimensionIds = React.useMemo(() => dimensions.flatMap((d) => d.value).map((d) => d.id), [dimensions]);

  const currentUser = useSelector(
    (store: AppState) => store.auth.loggedInUser,
    (left, right) => left.personId === right.personId
  );

  const invoiceNotes = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceNotes);
  const defaultLayout = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceLayout);

  const initialValues = React.useMemo(() => {
    let obj: InvoiceFormTypes.Create = {
      ownerId: currentUser.personId,
      currency: CurrencyEnums.CurrencyCode.SEK,
      isDimensionsAdvancedConfiguration: false,
      isPeriodisationAdvancedConfiguration: false,
      loadingCustomer: null,
      otherSettings: {
        invoiceDate: DateHelper.today(),
        dueDate: DateHelper.addDays(DateHelper.today(), 30),
        invoiceLayout: defaultLayout,
        languageKey: InvoiceConstants.DefaultInvoiceLanguageCode,
        sellerName: currentUser.name,
        notes: invoiceNotes,
      },
      recurringSettings: {} as InvoiceFormTypes.RecurringSettings,
    };

    if (quotation !== undefined) {
      const sortedRows = (quotation.rows ?? []).slice().sort((p1, p2) => p1.orderIndex - p2.orderIndex);
      const rowsWithUniqueId = sortedRows.map((row) => ({ row, uniqueId: UtilsHelper.getUniqueId() }));

      const houseWorkType = rowsWithUniqueId.some(({ row }) => row.rot)
        ? WintSuperkollAPIModel.InvoiceHouseWorkType.ROT
        : rowsWithUniqueId.some(({ row }) => row.rut)
          ? WintSuperkollAPIModel.InvoiceHouseWorkType.RUT
          : WintSuperkollAPIModel.InvoiceHouseWorkType.None;

      const rutRotArticles = rowsWithUniqueId
        .filter(({ row }) => row.rot || row.rut)
        .map(
          ({ row, uniqueId }): InvoiceFormTypes.RutRotArticleItem => ({
            id: uniqueId,
            name: row.overriddenDescription ?? row.description,
          })
        );

      obj = {
        ...obj,
        serialNumber: quotation.serialNumber,
        ownerId: quotation.ownerId,
        customer: { id: quotation.customerId, value: quotation.customerName ?? "" },
        referencePerson: {
          id: quotation.customerReference?.referenceId,
          value: quotation.customerReference?.referenceName ?? "",
        },
        total: sortedRows.map((p) => (p.price ?? 0) * p.quantity).reduce((a, b) => +a + +b, 0),
        tax: noVat ? 0 : quotation.totalTax,
        currency: quotation.currency ?? CurrencyEnums.CurrencyCode.SEK,

        products: rowsWithUniqueId.map(({ row: product, uniqueId }): InvoiceFormTypes.Product => {
          const standardPrice = product.overriddenPrice ?? product.price ?? 0;
          const vat = noVat ? 0 : (product.vAT as number);
          return {
            uniqueId,
            article: { id: product.articleId, value: product.description ?? product.overriddenDescription ?? "" },
            quantity: product.quantity,
            text: product.overriddenDescription ?? product.description,
            price: standardPrice,
            unit: product.unitId,
            tax: vat,
            taxText:
              vat === 0 ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree) : `${vat}%`,
            dimensions: quotation.dimensions?.filter((d) => allowedDimensionIds.includes(d)),
          };
        }),
        rutRot: {
          houseWorkType,
          rutRotArticles,
        },
        otherSettings: {
          ...obj.otherSettings,
          invoiceLayout: quotation.pdfTemplate,
          languageKey: quotation.language,
          showCurrencyCode: quotation.showCurrencyCode,
          sellerName: quotation.contactPerson,
          invoiceDate: quotation.date ?? new Date(),
          dueDate: quotation.endDate ?? obj.otherSettings.dueDate,
          notes: quotation.notes,
          attachments: quotation.attachments as UploadedFile[],
        },
      };
    }

    return obj;
  }, [quotation, currentUser, invoiceNotes]);

  return {
    initialValues,
    forcedDimensionMap,
  };
}

export default useQuotationInitialValues;
